import { useEffect, useRef, type ReactNode } from "react";

// The one modal shell for the app (Connections, Settings records, Region manager). Escape
// closes it (onClose) and focus lands on the element marked data-cancel (the Cancel / Close
// button) unless something inside, such as an autofocused field, already has focus.

export function AppModal({
  title,
  onClose,
  children,
  role = "dialog",
  headExtra,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  role?: "dialog" | "alertdialog";
  headExtra?: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const el = ref.current;
    if (el && !el.contains(document.activeElement)) {
      el.querySelector<HTMLElement>("[data-cancel]")?.focus();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        closeRef.current();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <div className="rf-modal-backdrop" role="presentation">
      <div className="rf-modal" role={role} aria-modal="true" aria-label={title} ref={ref}>
        <div className="rf-modal-head">
          <h3>{title}</h3>
          {headExtra}
        </div>
        {children}
      </div>
    </div>
  );
}

export interface ConfirmAction {
  label: string;
  kind: "primary" | "danger" | "outline";
  onClick: () => void;
  disabled?: boolean;
}

// A confirm: the message, the actions, then a Cancel that holds the initial focus.
export function ConfirmModal({
  title,
  message,
  actions,
  cancelLabel = "Cancel",
  onCancel,
}: {
  title: string;
  message: string;
  actions: ConfirmAction[];
  cancelLabel?: string;
  onCancel: () => void;
}) {
  return (
    <AppModal title={title} onClose={onCancel} role="alertdialog">
      <p>{message}</p>
      <div className="rf-modal-actions">
        {actions.map((a) => (
          <button key={a.label} type="button" className={`rf-btn rf-btn-${a.kind}`} disabled={a.disabled} onClick={a.onClick}>
            {a.label}
          </button>
        ))}
        <button type="button" className="rf-btn rf-btn-outline" data-cancel onClick={onCancel}>
          {cancelLabel}
        </button>
      </div>
    </AppModal>
  );
}
